/**
 * Builds a download-ready file name for an application attachment.
 *
 * A service name can end in a dot (e.g. "Renew Motor vehicle Insurance."),
 * which the browser treats as the start of a file extension, leaving the saved
 * file without a real extension. Callers also previously appended
 * `fileName.split('.').pop()` directly, which yields `pdf` (no dot) and
 * produces names like `...VRVTBUpdf`.
 *
 * This trims and strips any trailing dot(s) from the service name (interior
 * dots are preserved), joins only the parts that are present, and always
 * appends the real extension taken from the source file name — lower-cased and
 * falling back to `pdf`.
 */
export const buildAttachmentDownloadName = (
  serviceName: string | undefined | null,
  applicationNumber: string | undefined | null,
  sourceFileName?: string | null,
  fallbackExtension = 'pdf'
): string => {
  const trimmedServiceName = (serviceName || '').trim();
  let end = trimmedServiceName.length;
  while (end > 0 && trimmedServiceName.charAt(end - 1) === '.') {
    end--;
  }
  const cleanServiceName = trimmedServiceName.slice(0, end);
  const baseName = [cleanServiceName, applicationNumber?.trim()]
    .filter(Boolean)
    .join(' - ');

  const nameSegments = (sourceFileName || '').split('.');
  const extension =
    nameSegments.length > 1 ? nameSegments.pop()?.toLowerCase() : undefined;

  return `${baseName || 'download'}.${extension || fallbackExtension}`;
};
